import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  fetchThroughBrandAsync,
  setSubCategories,
} from "../../features/listingPage/listingPageSlice";
import { setSelectedSubcategory } from "../../store/slices/subCategorySlice";
import { setBreadcrumb } from "../../store/slices/breadCrumbsSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";

const ROTATION_MS = 10000;
const EASE = [0.2, 0.7, 0.2, 1];

const ExclusiveBrand = ({text,align}) => {
  const userDetail = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();

  // ---- Brand + banner mappings
  const banners = useMemo(
    () => [
      {
        key: "cratoni",
        brand: "cratoni",
        title: "Cratoni Helmets",
        subtitle: "Ride smarter. Ride safer.",
        cta: "Shop Helmets",
        offer: "Up to 35% OFF",
        img: "https://cyclecircle.blr1.cdn.digitaloceanspaces.com/Banners/cc-exclusive-2.webp",
        imgHidden:
          "https://cyclecircle.blr1.cdn.digitaloceanspaces.com/Banners/cc-exclusive-2.webp",
        navigateTo: "/listing-page/cratoni",
        tag: "Helmets",
      },
      {
        key: "moon",
        brand: "moon",
        title: "MOON Sports Lights",
        subtitle: "Brighter paths, longer rides.",
        cta: "Shop Lights",
        offer: "Up to 35% OFF",
        img: "https://cyclecircle.blr1.cdn.digitaloceanspaces.com/Banners/cc-exclusive-3.webp",
        imgHidden:
          "https://cyclecircle.blr1.cdn.digitaloceanspaces.com/Banners/cc-exclusive-3.webp",
        navigateTo: "/listing-page/moon%20sports",
        tag: "Lights",
      },
      {
        key: "funn",
        brand: "funn",
        title: "FUNN Cockpit",
        subtitle: "Precision control for every trail.",
        cta: "Shop Cockpit",
        offer: "Up to 35% OFF",
        img: "https://cyclecircle.blr1.cdn.digitaloceanspaces.com/Banners/cc-exclusive-1.webp",
        imgHidden:
          "https://cyclecircle.blr1.cdn.digitaloceanspaces.com/Banners/cc-exclusive-1.webp",
        navigateTo: "/listing-page/funn",
        tag: "Cockpit",
      },
      {
        key: "granite",
        brand: "granite",
        title: "Granite Tools",
        subtitle: "Pack light. Fix anything.",
        cta: "Shop Tools",
        offer: "Up to 35% OFF",
        img: "https://cyclecircle.blr1.cdn.digitaloceanspaces.com/Banners/cc-exclusive-4.webp",
        imgHidden:
          "https://cyclecircle.blr1.cdn.digitaloceanspaces.com/Banners/cc-exclusive-4.webp",
        navigateTo: "/listing-page/granite",
        tag: "Tools",
      },
    ],
    []
  );

  // Logo sources for tabs
  const logos = {
    cratoni: {
      src: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQohbUwx79PkQDbs4YO695jU0OXIAmfqRB-0jAxz05cag&s",
      alt: "Cratoni Logo",
      tag: "Helmets",
      navigateTo: "/listing-page/cratoni",
    },
    moon: {
      src: "https://www.handshake.fi/wp-content/uploads/2018/06/logo-moon.png",
      alt: "MOON Logo",
      tag: "Lights",
      navigateTo: "/listing-page/moon%20sports",
    },
    funn: {
      src: "https://images.bike24.com/i/mb/0c/67/20/logofunn-3903.jpg",
      alt: "FUNN Logo",
      tag: "Cockpit",
      navigateTo: "/listing-page/funn",
    },
    granite: {
      src: "https://www.stifmtb.com/cdn/shop/collections/Granite_logo_WH_1200x1200_13e278a3-8114-419c-94c0-0f6c6a0a86cc_1200x1200.jpg?v=1646754378",
      alt: "Granite Logo",
      tag: "Tools",
      navigateTo: "/listing-page/granite",
    },
  };

  // ---- State
  const [activeIdx, setActiveIdx] = useState(0);
  const [hoverBrand, setHoverBrand] = useState(banners[0].brand);
  const [isPaused, setIsPaused] = useState(false);

  const timerRef = useRef(null);
  const progressRef = useRef(0);
  const rafRef = useRef(null);
  const startedAtRef = useRef(performance.now());

  // ---- Helpers
  const goTo = (idx) => {
    setActiveIdx(idx);
    setHoverBrand(banners[idx].brand);
    progressRef.current = 0;
    startedAtRef.current = performance.now();
  };

  const next = () => goTo((activeIdx + 1) % banners.length);
  const prev = () => goTo((activeIdx - 1 + banners.length) % banners.length);

  // ---- Auto-rotate with smooth progress (respect reduced motion)
  useEffect(() => {
    if (shouldReduceMotion) return; // respect user preference
    if (isPaused) return;

    // progress animation via rAF for smooth progress bar
    const tick = (now) => {
      const elapsed = now - startedAtRef.current;
      progressRef.current = Math.min(1, elapsed / ROTATION_MS);
      if (progressRef.current >= 1) {
        next();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    // also a safety timeout to switch slide
    timerRef.current = setTimeout(next, ROTATION_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx, isPaused, shouldReduceMotion]);

  // Pause on hover/focus to improve UX
  const pauseCarousel = () => setIsPaused(true);
  const resumeCarousel = () => {
    setIsPaused(false);
    startedAtRef.current = performance.now();
  };

  const progressWidth = `${(progressRef.current || 0) * 100}%`;

  const handleFetchThroughBrands = (brand) => {
    dispatch(fetchThroughBrandAsync(brand)).then(() => {
      dispatch(setSubCategories([]));
      dispatch(setSelectedSubcategory(null));
      dispatch(
        setBreadcrumb({
          categoryID: null,
          categoryName: brand,
          subCategoryID: null,
          subCategoryName: "",
          productName: "",
          ProductID: null,
        })
      );
      navigate(`/listing-page/${brand}`);
    });
  };

  const handleBannerClick = (idx) => {
    const b = banners[idx];
    navigate(b.navigateTo);
  };

  const onKeyNav = (e) => {
    if (e.key === "ArrowRight") next();
    if (e.key === "ArrowLeft") prev();
  };

  // ---- Motion Variants
  const imgVariants = {
    enter: { opacity: 0, scale: 1.03 },
    center: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: EASE } },
    exit: {
      opacity: 0,
      scale: 0.995,
      transition: { duration: 0.5, ease: EASE },
    },
  };

  const overlayVariants = {
    hidden: { opacity: 0, y: 50 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", delay: 0.3 },
    },
    exit: {
      opacity: 0,
      y: 50,
      transition: { duration: 0.6 },
    },
  };

  const chipVariants = {
    rest: { y: 0, opacity: 0.9 },
    hover: { y: -2, opacity: 1 },
  };

  // ---- Render
  const currentBanner = banners[activeIdx];
  const showImg =
    Object.keys(userDetail || {}).length > 0
      ? currentBanner.img
      : currentBanner.imgHidden;

  return (
    <div className="w-full flex flex-col items-center justify-start bg-white-1 border-b border-gray-200">
      {/* Header */}
      <div className="w-full max-w-7xl">
        <div className="flex items-center justify-between pt-6">
          {/* Title */}
          <motion.span
            layout
            className={`flex-1 text-${align} py-4 text-3xl md:text-4xl font-extrabold tracking-wide 
      bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent 
      leading-tight`}
          >
            {text}
          </motion.span>

          {/* Navigation Controls */}
          <div className="hidden md:flex items-center gap-3 ml-6">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={prev}
              className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-xl 
        bg-white shadow-sm hover:bg-gray-100 hover:border-gray-400 
        transition duration-200 ease-in-out"
            >
              Prev
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={next}
              className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-xl 
        bg-white shadow-sm hover:bg-gray-100 hover:border-gray-400 
        transition duration-200 ease-in-out"
            >
              Next
            </motion.button>
          </div>
        </div>
      </div>

      {/* Hero / Banner */}
      <div
        className="w-full bg-white-1"
        onMouseEnter={pauseCarousel}
        onMouseLeave={resumeCarousel}
      >
        <div
          className="w-full relative overflow-hidden"
          onKeyDown={onKeyNav}
          tabIndex={0}
          aria-label="Exclusive brand banner"
        >
          <div className="w-full h-[350px] lg:h-[480px]">
            <AnimatePresence mode="sync">
              <motion.img
                key={currentBanner.key}
                src={showImg}
                alt={`${currentBanner.title} banner`}
                className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                onClick={() => handleBannerClick(activeIdx)}
                variants={imgVariants}
                initial="enter"
                animate="center"
                exit="exit"
                draggable={false}
              />
            </AnimatePresence>
          </div>

          {/* Soft gradient overlay for text legibility */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black-1/40 via-black-1/10 to-transparent" />

          {/* Animated overlay content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentBanner.key}-overlay`}
              variants={overlayVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              className="absolute inset-0 flex items-end"
            >
              <div className="w-full max-w-7xl px-5 pb-6 md:pb-8">
                <div className="inline-flex items-center gap-2 mb-2">
                  <motion.span
                    variants={chipVariants}
                    initial="rest"
                    whileHover="hover"
                    className="pointer-events-auto inline-flex items-center rounded-full bg-white-1/90 text-black-1 px-3 py-1 text-xs font-medium shadow-md backdrop-blur"
                  >
                    {currentBanner.tag}
                  </motion.span>
                  <motion.span
                    variants={chipVariants}
                    initial="rest"
                    whileHover="hover"
                    className="pointer-events-auto inline-flex items-center rounded-full bg-black-1/60 text-white px-3 py-1 text-xs font-medium shadow-md"
                  >
                    {currentBanner.offer}
                  </motion.span>
                </div>

                <div className="max-w-xl">
                  <h2 className="text-white text-2xl md:text-4xl font-extrabold leading-tight drop-shadow-sm">
                    {currentBanner.title}
                  </h2>
                  <p className="mt-1 md:mt-2 text-white/90 text-sm md:text-base">
                    {currentBanner.subtitle}
                  </p>

                  <motion.button
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleBannerClick(activeIdx)}
                    className="pointer-events-auto mt-3 md:mt-4 inline-flex items-center gap-2 rounded-2xl bg-white-1 text-gray-900 px-4 py-2 text-sm font-semibold shadow-lg hover:shadow-xl"
                  >
                    {currentBanner.cta}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="opacity-70"
                    >
                      <path
                        d="M5 12h14M13 5l7 7-7 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white-1/30 backdrop-blur-sm">
            <motion.div
              key={`progress-${activeIdx}-${isPaused ? "pause" : "run"}`}
              className="h-full bg-white"
              style={{ width: progressWidth }}
              animate={{
                width: isPaused ? progressWidth : "100%",
              }}
              transition={
                shouldReduceMotion || isPaused
                  ? { duration: 0 }
                  : { duration: ROTATION_MS / 1000, ease: "linear" }
              }
            />
          </div>

          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {banners.map((b, i) => (
              <button
                key={b.key}
                onClick={() => goTo(i)}
                aria-label={`Go to ${b.title}`}
                className={`h-2.5 rounded-full transition-all ${
                  i === activeIdx
                    ? "w-6 bg-white-1"
                    : "w-2.5 bg-white-1/60 hover:bg-white-1/80"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Brand Tabs */}
      <div className="w-full bg-white-1">
        <div className="w-full mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {banners.map((b, idx) => {
              const isActive = hoverBrand === b.brand;
              const logo = logos[b.brand];
              return (
                <motion.button
                  key={b.key}
                  onMouseEnter={() => {
                    setHoverBrand(b.brand);
                    goTo(idx);
                  }}
                  onFocus={() => {
                    setHoverBrand(b.brand);
                    goTo(idx);
                  }}
                  onClick={() =>
                    handleFetchThroughBrands(
                      b.brand === "moon" ? "moon sports" : b.brand
                    )
                  }
                  className="relative group w-full h-24 md:h-24 rounded-xl border bg-white-1 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* subtle background pulse */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-white-1 via-white-1 to-gray-50"
                    animate={
                      isActive ? { opacity: [0.9, 1, 0.9] } : { opacity: 1 }
                    }
                    transition={
                      isActive ? { repeat: Infinity, duration: 2 } : {}
                    }
                  />

                  {/* underline progress mimic */}
                  <motion.div
                    key={isActive ? `${b.key}-progress` : `${b.key}-idle`}
                    className="absolute bottom-0 left-0 h-1.5 bg-gradient-to-r from-orange-500 to-yellow-400"
                    initial={{ width: 0 }}
                    animate={{ width: isActive ? "100%" : 0 }}
                    transition={{ duration: isActive ? 10 : 0 }}
                  />

                  {/* Center Container */}
                  <div className="relative z-10 h-full w-full flex justify-center">
                    {/* Logo */}
                    <img
                      loading="lazy"
                      src={logo.src}
                      alt={logo.alt}
                      className={`h-16 md:h-16 -mt-2 object-contain transition-transform duration-500 ${
                        isActive ? "scale-[1.04]" : "scale-100"
                      }`}
                    />

                    {/* Tag (absolute under logo) */}
                    <span className="absolute top-[45%] text-xs font-semibold text-gray-900">
                      {logo.tag}
                    </span>

                    {/* Offer (absolute under tag) */}
                    <span
                      className={`absolute top-[70%] text-[10px] tracking-wide font-medium transition-colors ${
                        isActive ? "text-orange-500" : "text-gray-600"
                      }`}
                    >
                      {b.offer}
                    </span>
                  </div>

                  {/* Corner badge on hover */}
                  <motion.div
                    className="pointer-events-none absolute top-2 right-2 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-black-1/70 text-white-1 shadow"
                    initial={{ opacity: 0, y: -6 }}
                    animate={{
                      opacity: isActive ? 1 : 0,
                      y: isActive ? 0 : -6,
                    }}
                    transition={{ duration: 0.25 }}
                  >
                    {b.brand.toUpperCase()}
                  </motion.div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExclusiveBrand;
